import gettext from '@eb/gettext';
import { isEmpty } from 'lodash';
import momentTimezone from 'js-utils/moment-timezone';
import { getAPIDateTimeFormats } from 'js-utils/datetime';

import {
    API_STATUS_CANCELED,
    API_STATUS_DRAFT,
    EVENT_SALES_STATUS_ICON_GREY_COLOR,
    EVENT_SALES_STATUS_ICON_RED_COLOR,
    EVENT_SALES_STATUS_ICON_YELLOW_COLOR,
    EVENT_SALES_STATUS_ICON_COLOR_MAP,
    EVENT_SALES_STATUS_MESSAGE_TYPE_CUSTOM,
    EVENT_SALES_STATUS_ON_SALE,
    EVENT_SALES_STATUS_NOT_YET_ON_SALE,
    SCHEDULE_PUBLISH_STATUS_SCHEDULED,
    EVENT_SALES_STATUS_MESSAGE_CODE_AT_THE_DOOR,
    DONATION_PRICE_CHANGE_NOTIFIED_COUNTRIES,
    DONATION_PRICE_CHANGE_PLAYBOOK,
    DONATION_PRICE_CHANGE_PLAYBOOK_COUNTRIES,
    DONATION_PRICE_CHANGE_SIMPLE,
    PLAYBOOK_ONLY,
    PLAYBOOK_ONLY_COUNTRIES,
} from '../constants';

/**
 * Returns venue display label for an event
 *
 * Will return undefined if an event is "To be announced" (doesn't have a venue and isn't online)
 *
 * @param {Object} venueInfo - Normalized subset of venue information from API event response
 * @param {Object} [venueInfo.venue] - Venue information including name and address
 * @param {string} venueInfo.venue.name - Name of the venue
 * @param {boolean} venueInfo.onlineEvent - Whether or not the event is an online event
 * @returns {string} The venue display label
 */
export const getVenueDisplay = (venueInfo) => {
    const { venue, onlineEvent } = venueInfo;
    let venueDisplay;

    if (venue) {
        venueDisplay = venue.name;
    } else if (onlineEvent) {
        venueDisplay = gettext('Online event');
    }

    return venueDisplay;
};

/**
 * Returns the icon color for the event sales status
 *
 * If an unknown status is passed, the default grey color is returned.
 *
 * @param {object} options
 * @param {boolean} options.isScheduledPublish - Whether or not the event is scheduled to publish
 * @param {import('../fixtures').EventSalesStatus} options.salesStatus - Sales status of the event
 * @param {import('../fixtures').EventStatus} options.status - Event status
 * @returns {string} The color of the icon
 */
export const getSalesStatusIconColor = ({
    isScheduledPublish,
    salesStatus,
    status,
}) => {
    if (isScheduledPublish) {
        return EVENT_SALES_STATUS_ICON_YELLOW_COLOR;
    } else if (status === API_STATUS_CANCELED) {
        return EVENT_SALES_STATUS_ICON_RED_COLOR;
    } else if (EVENT_SALES_STATUS_ICON_COLOR_MAP[salesStatus]) {
        return EVENT_SALES_STATUS_ICON_COLOR_MAP[salesStatus];
    }

    return EVENT_SALES_STATUS_ICON_GREY_COLOR;
};

/**
 * Returns true if the message type is custom
 *
 * @param {import('../fixtures').EventSalesMessageType} messageType - The type of message of the sales status
 * @returns {boolean} True if the message type is custom
 */
export const isCustomSalesStatus = ({ messageType }) =>
    messageType === EVENT_SALES_STATUS_MESSAGE_TYPE_CUSTOM;

/**
 * Returns true if the event is draft
 * @param {import('../fixtures').EventStatus} status - event status
 * @returns {boolean} True if the event is draft
 */
export const eventIsDraft = (status) => status === API_STATUS_DRAFT;

/**
 * Returns the message to display for the event sales status.
 *
 * By default returns the sales message.
 *
 * @param {object} options
 * @param {import('../fixtures').EventStatus} options.status - Event status
 * @param {import('../fixtures').EventSalesStatus} options.salesStatus - Event sales status
 * @param {string} options.message - Sales status message returned by the backend
 * @param {import('../fixtures').EventSalesMessageType} options.messageType - Sales status type of message
 * @param {import('../fixtures').EventSalesMessageCode} options.messageCode - The message code of the event sales status
 * @param {boolean} options.isScheduledPublish - Whether or not the event is scheduled to publish
 * @returns {string} The message to display
 */
export const getSalesStatusLabel = ({
    status,
    salesStatus,
    message,
    messageType,
    messageCode,
    isScheduledPublish,
}) => {
    if (isScheduledPublish) {
        return gettext('Scheduled');
    } else if (eventIsDraft(status)) {
        return gettext('Draft');
    } else if (status === API_STATUS_CANCELED) {
        return gettext('Canceled');
    } else if (isCustomSalesStatus({ messageType })) {
        return gettext('Custom');
    } else if (
        salesStatus === EVENT_SALES_STATUS_ON_SALE ||
        salesStatus === EVENT_SALES_STATUS_NOT_YET_ON_SALE
    ) {
        return gettext('On Sale');
    } else if (messageCode === EVENT_SALES_STATUS_MESSAGE_CODE_AT_THE_DOOR) {
        return gettext('At The Door');
    }

    return message;
};

/**
 * Returns true if the event is scheduled to publish
 *
 * @param {object} publishSettings - Publish settings of the event
 * @returns {boolean} True if the event is scheduled
 */
export const eventIsScheduledPublish = (publishSettings) => {
    let isScheduledPublish = false;

    if (!isEmpty(publishSettings)) {
        const { schedulePublishStatus, schedulePublishDate } = publishSettings;
        const statusIsScheduled =
            schedulePublishStatus === SCHEDULE_PUBLISH_STATUS_SCHEDULED;

        if (statusIsScheduled && schedulePublishDate) {
            const utcPublishDate = momentTimezone(schedulePublishDate);

            // We check that the schedule publish date is in the future to avoid possible errors.
            isScheduledPublish =
                utcPublishDate.diff(momentTimezone().utc()) > 0;
        }
    }

    return isScheduledPublish;
};

/**
 * Returns true if the event is not yet on sale
 *
 * @param {object} eventSalesStatus - Sales status of the event
 * @returns {boolean} True if the event is not yet on sale
 */
export const eventIsNotYetOnSale = (eventSalesStatus) =>
    !isEmpty(eventSalesStatus) &&
    eventSalesStatus.salesStatus === EVENT_SALES_STATUS_NOT_YET_ON_SALE;

/**
 * Returns a string with the formatted status date and time
 *
 * @param {string} utc - The datetime in UTC
 * @param {string} timezone - The timezone of the local datetime to format into
 * @returns {string} Formatted date and time - I.e. "Jan 28 • 12:00 AM"
 */
export const getFormattedStatusDate = ({ utc, timezone }) => {
    let formattedDate = '';

    if (utc && timezone) {
        const formattedAPIDateTime = getAPIDateTimeFormats({ utc, timezone });

        formattedDate = `${formattedAPIDateTime.shortDate} • ${formattedAPIDateTime.shortTime}`;
    }

    return formattedDate;
};

/**
 * Returns true if it should show the event sales status icon
 *
 * @param {object} options
 * @param {boolean} options.isScheduledPublish - Indicates if the event is scheduled to be published
 * @param {object} options.eventSalesStatus - Sales status of the event
 * @param {import('../fixtures').EventStatus} options.eventStatus - Event status
 * @returns {boolean} True if the event is not yet on sale
 */
export const shouldShowSalesStatusIcon = ({
    isScheduledPublish,
    eventSalesStatus,
    eventStatus,
}) =>
    isScheduledPublish ||
    (!isEmpty(eventSalesStatus) && eventStatus !== API_STATUS_DRAFT);

/**
 * Returns the visibility info of the event
 *
 * @param {object} statusInfo - Normalized subset of status information from API event response
 * @returns {object} Object with the visibility and label texts.
 */
export const getSalesStatusVisibilityInfo = (statusInfo) => {
    const { listed = true, inviteOnly = false, password } = statusInfo;
    let label = '';
    let visibility = '';

    if (password) {
        visibility = gettext('Password-protected');
    } else if (inviteOnly) {
        visibility = gettext('Invitation-only');
    } else if (!listed) {
        visibility = gettext('Hidden');
    }

    if (visibility) {
        label = gettext('Private');
    }

    return {
        label,
        visibility,
    };
};

/**
 * Returns type of notification to show for donations, if any
 *
 * @param {object} checkoutSettings - The organizer's checkout settings
 * @returns {string} String for playbook notification, simple or empty string for none
 */
export const showDonationsBannerType = (checkoutSettings = {}) => {
    const methodSettings = Object.values(checkoutSettings).find(
        ({ countryCode }) =>
            countryCode &&
            DONATION_PRICE_CHANGE_NOTIFIED_COUNTRIES.includes(countryCode),
    );
    let showNotification = '';

    if (methodSettings) {
        const countryCode = methodSettings['countryCode'];
        showNotification = DONATION_PRICE_CHANGE_SIMPLE;

        if (DONATION_PRICE_CHANGE_PLAYBOOK_COUNTRIES.includes(countryCode)) {
            showNotification = DONATION_PRICE_CHANGE_PLAYBOOK;
        } else if (PLAYBOOK_ONLY_COUNTRIES.includes(countryCode)) {
            showNotification = PLAYBOOK_ONLY;
        }
    }

    return showNotification;
};
